import React from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ServiceRequestStatusCode, AcceptedOfferSummary } from "../../models/models";

// ===================================================================
//  Props
// ===================================================================
interface RfsStatusGuidanceBannerProps {
  statusCode: number;
  offersCount: number;
  newOffersCount: number;
  acceptedOffer?: AcceptedOfferSummary | null;
  hasReviewed: boolean;
  onScrollToOffers?: () => void;
  onScrollToProgress?: () => void;
}

// ===================================================================
//  Banner configuration per status
// ===================================================================
interface BannerConfig {
  accentColor: string;
  bgColor: string;
  borderColor: string;
  iconBgColor: string;
  icon: React.ReactNode;
  messageKey: string;
  messageParams?: Record<string, string | number>;
  subtextKey?: string;
  actionLabelKey?: string;
  actionGradient?: string;
  actionGradientHover?: string;
  actionTextColor?: string;
  onAction?: "offers" | "progress";
}

const getBannerConfig = (
  statusCode: number,
  offersCount: number,
  _newOffersCount: number,
  acceptedOffer: AcceptedOfferSummary | null | undefined,
  hasReviewed: boolean
): BannerConfig | null => {
  switch (statusCode) {
    case ServiceRequestStatusCode.openForOffers:
      return {
        accentColor: "#3498DB",
        bgColor: "rgba(52, 152, 219, 0.04)",
        borderColor: "rgba(52, 152, 219, 0.15)",
        iconBgColor: "rgba(52, 152, 219, 0.10)",
        icon: <SearchOutlinedIcon sx={{ color: "#3498DB", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.openToOffers",
        subtextKey: undefined,
        actionLabelKey: undefined,
        onAction: undefined,
      };

    case ServiceRequestStatusCode.negotiating:
      return {
        accentColor: "#3498DB",
        bgColor: "rgba(52, 152, 219, 0.06)",
        borderColor: "rgba(52, 152, 219, 0.18)",
        iconBgColor: "rgba(52, 152, 219, 0.12)",
        icon: <InboxOutlinedIcon sx={{ color: "#2980B9", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.negotiating",
        messageParams: { count: offersCount },
        actionLabelKey: "servicing:rfsDetail.guidanceBanner.viewOffersAction",
        actionGradient: "linear-gradient(135deg, #3498DB, #2980B9)",
        actionGradientHover: "linear-gradient(135deg, #2980B9, #3498DB)",
        actionTextColor: "#FFFFFF",
        onAction: "offers",
      };

    case ServiceRequestStatusCode.awaitingProviderConfirmation: {
      const providerName = acceptedOffer?.providerName || "";
      return {
        accentColor: "#F59E0B",
        bgColor: "rgba(245, 158, 11, 0.04)",
        borderColor: "rgba(245, 158, 11, 0.18)",
        iconBgColor: "rgba(245, 158, 11, 0.10)",
        icon: <AccessTimeOutlinedIcon sx={{ color: "#D97706", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.awaitingProviderConfirmation",
        messageParams: providerName ? { provider: providerName } : undefined,
        actionLabelKey: "servicing:rfsDetail.guidanceBanner.viewOfferDetailsAction",
        actionGradient: "linear-gradient(135deg, #F59E0B, #D97706)",
        actionGradientHover: "linear-gradient(135deg, #D97706, #F59E0B)",
        actionTextColor: "#FFFFFF",
        onAction: "offers",
      };
    }

    case ServiceRequestStatusCode.inProgress:
      return {
        accentColor: "#10B981",
        bgColor: "rgba(16, 185, 129, 0.04)",
        borderColor: "rgba(16, 185, 129, 0.15)",
        iconBgColor: "rgba(16, 185, 129, 0.10)",
        icon: <BuildOutlinedIcon sx={{ color: "#10B981", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.inProgress",
        actionLabelKey: "servicing:rfsDetail.guidanceBanner.viewProgressAction",
        actionGradient: "linear-gradient(135deg, #10B981, #059669)",
        actionGradientHover: "linear-gradient(135deg, #059669, #10B981)",
        actionTextColor: "#FFFFFF",
        onAction: "progress",
      };

    case ServiceRequestStatusCode.awaitingCompletionConfirmation:
      return {
        accentColor: "#F97316",
        bgColor: "rgba(249, 115, 22, 0.04)",
        borderColor: "rgba(249, 115, 22, 0.18)",
        iconBgColor: "rgba(249, 115, 22, 0.10)",
        icon: <CheckCircleOutlinedIcon sx={{ color: "#EA580C", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.awaitingCompletion",
        actionLabelKey: "servicing:rfsDetail.guidanceBanner.reviewCompletionAction",
        actionGradient: "linear-gradient(135deg, #F97316, #EA580C)",
        actionGradientHover: "linear-gradient(135deg, #EA580C, #F97316)",
        actionTextColor: "#FFFFFF",
        onAction: "progress",
      };

    case ServiceRequestStatusCode.completed:
      if (!hasReviewed) {
        return {
          accentColor: "#8B5CF6",
          bgColor: "rgba(139, 92, 246, 0.04)",
          borderColor: "rgba(139, 92, 246, 0.15)",
          iconBgColor: "rgba(139, 92, 246, 0.10)",
          icon: <StarOutlinedIcon sx={{ color: "#7C3AED", fontSize: 20 }} />,
          messageKey: "servicing:rfsDetail.guidanceBanner.completedNoReview",
          actionLabelKey: "servicing:rfsDetail.guidanceBanner.leaveReviewAction",
          actionGradient: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
          actionGradientHover: "linear-gradient(135deg, #7C3AED, #8B5CF6)",
          actionTextColor: "#FFFFFF",
          onAction: "progress",
        };
      }
      return {
        accentColor: "#10B981",
        bgColor: "rgba(16, 185, 129, 0.04)",
        borderColor: "rgba(16, 185, 129, 0.15)",
        iconBgColor: "rgba(16, 185, 129, 0.10)",
        icon: <CheckCircleOutlinedIcon sx={{ color: "#10B981", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.completedReviewed",
        actionLabelKey: undefined,
        onAction: undefined,
      };

    case ServiceRequestStatusCode.archived:
      return {
        accentColor: "#9CA3AF",
        bgColor: "rgba(156, 163, 175, 0.04)",
        borderColor: "rgba(156, 163, 175, 0.15)",
        iconBgColor: "rgba(156, 163, 175, 0.10)",
        icon: <InfoOutlinedIcon sx={{ color: "#6B7280", fontSize: 20 }} />,
        messageKey: "servicing:rfsDetail.guidanceBanner.archived",
        actionLabelKey: undefined,
        onAction: undefined,
      };

    // Draft — no banner
    case ServiceRequestStatusCode.draft:
    default:
      return null;
  }
};

// ===================================================================
//  Component
// ===================================================================
const RfsStatusGuidanceBanner: React.FC<RfsStatusGuidanceBannerProps> = ({
  statusCode,
  offersCount,
  newOffersCount,
  acceptedOffer,
  hasReviewed,
  onScrollToOffers,
  onScrollToProgress,
}) => {
  const { translate } = useTranslation(["servicing"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  const config = getBannerConfig(statusCode, offersCount, newOffersCount, acceptedOffer, hasReviewed);

  if (!config) return null;

  const handleAction = () => {
    if (config.onAction === "offers" && onScrollToOffers) {
      onScrollToOffers();
    } else if (config.onAction === "progress" && onScrollToProgress) {
      onScrollToProgress();
    }
  };

  const messageText = config.messageParams
    ? translate(config.messageKey, config.messageParams)
    : translate(config.messageKey);

  const hasAction = config.actionLabelKey && config.onAction;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: isPhone ? "flex-start" : "center",
        flexDirection: isPhone ? "column" : "row",
        gap: isPhone ? 1.5 : 2,
        p: 2,
        borderRadius: dt.radius,
        backgroundColor: config.bgColor,
        border: `1px solid ${config.borderColor}`,
        borderLeft: `4px solid ${config.accentColor}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
        animation: "sectionReveal 0.4s ease-out both",
        transition: "all 0.2s ease",
      }}
    >
      {/* Icon circle */}
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: "50%",
          backgroundColor: config.iconBgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {config.icon}
      </Box>

      {/* Message */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.textPrimary,
            letterSpacing: "-0.1px",
            lineHeight: 1.5,
          }}
        >
          {messageText}
        </Box>
      </Box>

      {/* Action button */}
      {hasAction && (
        <Button
          onClick={handleAction}
          sx={{
            background: config.actionGradient,
            color: config.actionTextColor,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontWeight: 700,
            fontFamily: dt.font,
            fontSize: "12px",
            px: 2.5,
            py: 0.8,
            whiteSpace: "nowrap",
            minWidth: "auto",
            boxShadow: `0 2px 8px ${config.accentColor}33`,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: config.actionGradientHover,
              transform: "translateY(-1px)",
              boxShadow: `0 4px 14px ${config.accentColor}40`,
            },
          }}
        >
          {translate(config.actionLabelKey as string)}
        </Button>
      )}
    </Box>
  );
};

export default RfsStatusGuidanceBanner;
